
import i18n from "@/i18n";/**
 * WashPros Legal & Trust Policy Center — original content.
 *
 *   WashPros® · The Car Wash Concierge Company™ · Wax & Shine Headquarters™
 *   Operated by WashPros Global Services LLC.
 *   WashPros is a registered fictitious name / DBA in the State of Florida.
 *
 * Every block of copy below was drafted from scratch for WashPros. No
 * text was copied or paraphrased from another marketplace, platform, or
 * third-party policy. Effective dates are static "May 16, 2026" until
 * legal counsel reviews and approves changes.
 *
 * Section schema:
 *   { heading: string, body?: string[] (paragraphs), items?: string[] (bullets) }
 *
 * Shell renders sections in order; `body` paragraphs precede `items` bullets.
 */

export const COMPANY = {
  get legalName() { return i18n.t("legal.washpros_global_services_llc"); },
  get dbaNote() { return i18n.t("legal.washpros_is_a_registered_fictitious_name_2"); },
  address: "6800 Bird Rd Suite 454, Miami, FL 33155",
  legalEmail: "legal@washpros.io",
  supportEmail: "hello@washpros.io",
  website: "washpros.io",
  effective: "May 16, 2026",
  version: "1.0",
};

// Common related-link sets for quick reuse.
const REL = {
  terms: { to: "/terms", get label() { return i18n.t("legal.terms_of_service"); } },
  privacy: { to: "/privacy", get label() { return i18n.t("legal.privacy_policy"); } },
  copyright: { to: "/copyright-policy", get label() { return i18n.t("legal.copyright_notification_policy"); } },
  cookie: { to: "/cookie-policy", get label() { return i18n.t("legal.cookie_policy"); } },
  dataSec: { to: "/data-security", get label() { return i18n.t("legal.data_security_summary"); } },
  retention: { to: "/data-retention", get label() { return i18n.t("legal.data_retention_policy"); } },
  comms: { to: "/communications-policy", get label() { return i18n.t("legal.communications_policy"); } },
  refund: { to: "/booking-refund-dispute-policy", get label() { return i18n.t("legal.booking_refund_dispute_policy"); } },
  vendor: { to: "/vendor-marketplace-standards", get label() { return i18n.t("legal.vendor_marketplace_standards"); } },
  payout: { to: "/vendor-payout-policy", get label() { return i18n.t("legal.vendor_payout_policy"); } },
  trademark: { to: "/trademark-policy", get label() { return i18n.t("legal.trademark_policy"); } },
  reviews: { to: "/reviews-policy", get label() { return i18n.t("legal.reviews_policy"); } },
  influencer: { to: "/influencer-disclosure", get label() { return i18n.t("legal.influencer_affiliate_disclosure"); } },
  ai: { to: "/ai-disclosure", get label() { return i18n.t("legal.ai_disclosure"); } },
  photo: { to: "/photo-media-policy", get label() { return i18n.t("legal.photo_media_policy"); } },
  damage: { to: "/property-damage-policy", get label() { return i18n.t("legal.property_damage_policy"); } },
  subs: { to: "/subscription-terms", get label() { return i18n.t("legal.subscription_terms"); } },
  acceptable: { to: "/acceptable-use", get label() { return i18n.t("legal.acceptable_use_policy"); } },
  dmca: { to: "/dmca-agent", get label() { return i18n.t("legal.dmca_designated_agent"); } },
};

export const POLICIES = {
  // ─────────────────────────────────────────────────────────────
  "acceptable-use": {
    id: "acceptable-use",
    get title() { return i18n.t("legal.acceptable_use_policy"); },
    get badge() { return i18n.t("legal.trust_safety"); },
    badgeIcon: "ShieldAlert",
    get summary() { return i18n.t("legal.rules_every_customer_vendor_contractor_par"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_who_this_policy_applies_to"); },
        get body() { return [
          i18n.t("legal.this_acceptable_use_policy_the_aup"),
          i18n.t("legal.if_you_use_the_platform_on"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_prohibited_conduct"); },
        get body() { return [
          i18n.t("legal.you_agree_not_to_engage_in"),
        ]; },
        get items() { return [
          i18n.t("legal.fraud_money_laundering_chargeback_abuse_fa"),
          i18n.t("legal.harassment_threats_slurs_intimidation_sexu"),
          i18n.t("legal.impersonation_of_another_person_business_o"),
          i18n.t("legal.scraping_mirroring_mass_downloading_automa"),
          i18n.t("legal.reverse_engineering_decompiling_or_attempt"),
          i18n.t("legal.off_platform_circumvention_contacting_a_wa"),
          i18n.t("legal.posting_fake_paid_and_undisclosed_retaliat"),
          i18n.t("legal.uploading_material_that_infringes_another"),
          i18n.t("legal.unsafe_service_conduct_performing_washes_o"),
          i18n.t("legal.sharing_or_transmitting_malware_phishing_p"),
          i18n.t("legal.spamming_unsolicited_bulk_messaging_seo_li"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_booking_integrity"); },
        get body() { return [
          i18n.t("legal.bookings_exist_to_connect_a_real"),
          "WashPros operates a separate QA environment with synthetic test accounts for legitimate testing; ask hello@washpros.io if you need access.",
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_enforcement"); },
        get body() { return [
          i18n.t("legal.washpros_may_at_its_sole_discretion"),
          i18n.t("legal.washpros_may_preserve_evidence_of_suspecte"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_reporting"); },
        body: [
          "If you witness or experience conduct that violates this AUP, report it to hello@washpros.io with as much detail as you can safely provide (booking ID, vendor name, time of incident, screenshots). For threats to life or safety, call 911 first, then notify WashPros.",
        ],
      },
    ],
    related: [REL.terms, REL.privacy, REL.vendor, REL.refund, REL.comms, REL.reviews, REL.ai],
  },

  // ─────────────────────────────────────────────────────────────
  "vendor-marketplace-standards": {
    id: "vendor-marketplace-standards",
    get title() { return i18n.t("legal.vendor_marketplace_standards"); },
    get badge() { return i18n.t("legal.vendor_operations"); },
    badgeIcon: "Wrench",
    get summary() { return i18n.t("legal.quality_insurance_identity_and_conduct_sta"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_eligibility_onboarding"); },
        get body() { return [
          i18n.t("legal.before_being_approved_as_a_washpros"),
        ]; },
        get items() { return [
          i18n.t("legal.valid_government_issued_photo_id_for"),
          i18n.t("legal.valid_driver_license_for_any_operator"),
          i18n.t("legal.active_business_registration_in_the_state"),
          i18n.t("legal.active_certificate_of_insurance_evidencing"),
          i18n.t("legal.approved_bank_account_ach_or_payout"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_required_insurance"); },
        get body() { return [
          i18n.t("legal.washpros_requires_every_active_mobile_vend"),
        ]; },
        get items() { return [
          i18n.t("legal.commercial_general_liability_cgl_minimum_u"),
          i18n.t("legal.garagekeepers_legal_liability_applicable_t"),
          i18n.t("legal.auto_liability_for_any_vendor_owned"),
          i18n.t("legal.workers_compensation_where_required_by_flo"),
        ]; },
        get bodyAfter() { return [
          i18n.t("legal.coverage_lapses_must_be_reported_to"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_service_quality_standards"); },
        get body() { return [
          i18n.t("legal.vendors_must_perform_every_washpros_bookin"),
        ]; },
        get items() { return [
          i18n.t("legal.arrive_within_the_booking_window_or"),
          i18n.t("legal.inspect_the_vehicle_with_the_customer"),
          i18n.t("legal.use_water_conscious_practices_and_surface"),
          i18n.t("legal.leave_the_customer_property_clean_pick"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_customer_communication"); },
        get body() { return [
          i18n.t("legal.all_communication_with_a_washpros_customer"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_photo_documentation"); },
        get body() { return [
          i18n.t("legal.vendors_must_capture_pre_service_and"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s6_cancellations_no_shows_and_reschedules"); },
        get body() { return [
          i18n.t("legal.vendor_initiated_cancellations_after_the_b"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s7_suspension_removal"); },
        get body() { return [
          i18n.t("legal.washpros_may_suspend_or_remove_a"),
        ]; },
      },
    ],
    related: [REL.acceptable, REL.payout, REL.comms, REL.photo, REL.damage, REL.copyright, REL.terms],
  },

  // ─────────────────────────────────────────────────────────────
  "booking-refund-dispute-policy": {
    id: "booking-refund-dispute-policy",
    get title() { return i18n.t("legal.booking_refund_dispute_policy"); },
    get badge() { return i18n.t("legal.customer_operations"); },
    badgeIcon: "ReceiptText",
    get summary() { return i18n.t("legal.how_washpros_charges_cancels_refunds_and"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_when_you_are_charged"); },
        get body() { return [
          i18n.t("legal.when_you_book_a_service_through"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_customer_cancellations"); },
        get body() { return [
          i18n.t("legal.customers_may_cancel_a_booking_at"),
          i18n.t("legal.cancellations_made_after_a_vendor_has"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_weather_force_majeure"); },
        get body() { return [
          i18n.t("legal.heavy_rain_lightning_hurricane_warnings_ha"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_no_shows_vendor_late_arrival"); },
        get body() { return [
          i18n.t("legal.customer_no_show_if_a_vendor"),
          i18n.t("legal.vendor_late_arrival_if_a_vendor"),
        ]; },
      },
      {
        heading: "5. Refunds",
        get body() { return [
          i18n.t("legal.refunds_are_issued_back_to_the"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s6_damage_claims"); },
        get body() { return [
          i18n.t("legal.if_you_believe_your_property_was"),
        ]; },
      },
      {
        heading: "7. Disputes",
        get body() { return [
          i18n.t("legal.if_a_customer_and_vendor_disagree"),
          i18n.t("legal.customers_retain_any_rights_they_have"),
        ]; },
      },
    ],
    related: [REL.damage, REL.acceptable, REL.comms, REL.ai, REL.subs, REL.terms, REL.privacy],
  },

  // ─────────────────────────────────────────────────────────────
  "communications-policy": {
    id: "communications-policy",
    get title() { return i18n.t("legal.communications_policy"); },
    get badge() { return i18n.t("legal.messaging_consent"); },
    badgeIcon: "MessageCircle",
    get summary() { return i18n.t("legal.how_washpros_communicates_with_customers_v"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_communication_channels"); },
        get body() { return [
          i18n.t("legal.washpros_may_communicate_with_you_through"),
        ]; },
        get items() { return [
          i18n.t("legal.email_sent_via_resend_or_another"),
          i18n.t("legal.phone_outbound_from_a_published_washpros"),
          i18n.t("legal.sms_only_when_you_have_provided"),
          i18n.t("legal.push_notifications_only_when_you_have"),
          i18n.t("legal.in_app_chat_inside_the_customer"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_transactional_vs_marketing_messages"); },
        get body() { return [
          i18n.t("legal.transactional_messages_relate_to_a_specifi"),
          i18n.t("legal.marketing_messages_promotions_new_service"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_sms_consent"); },
        get body() { return [
          i18n.t("legal.washpros_does_not_send_marketing_sms"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_ai_phone_receptionist"); },
        get body() { return [
          i18n.t("legal.calls_to_washpros_may_be_answered"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_anti_spam_commitment"); },
        get body() { return [
          i18n.t("legal.washpros_does_not_sell_email_addresses"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s6_how_to_opt_out"); },
        body: [
          "Email: unsubscribe link at the bottom of any marketing email, or email hello@washpros.io with the address to remove. SMS: reply STOP. Push: revoke at the OS level. Phone: ask the AI receptionist or human agent to add your number to the do-not-call list during any call. All opt-out requests are honored within ten (10) business days; transactional messages may continue while an active booking is in progress.",
        ],
      },
    ],
    related: [REL.privacy, REL.ai, REL.cookie, REL.retention, REL.acceptable, REL.terms],
  },

  // ─────────────────────────────────────────────────────────────
  "data-security": {
    id: "data-security",
    get title() { return i18n.t("legal.data_security_summary"); },
    badge: "Security",
    badgeIcon: "Shield",
    get summary() { return i18n.t("legal.plain_english_summary_of_the_technical"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_security_philosophy"); },
        get body() { return [
          i18n.t("legal.washpros_treats_customer_vendor_and_paymen"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_reasonable_safeguards"); },
        get body() { return [
          i18n.t("legal.washpros_maintains_commercially_reasonable"),
        ]; },
        get items() { return [
          i18n.t("legal.transport_layer_encryption_https_tls_on"),
          i18n.t("legal.encryption_at_rest_for_primary_databases"),
          i18n.t("legal.role_based_access_control_inside_the"),
          i18n.t("legal.least_privilege_secrets_management_product"),
          i18n.t("legal.multi_factor_authentication_or_magic_link"),
          i18n.t("legal.brute_force_and_rate_limit_protection"),
          i18n.t("legal.routine_dependency_scanning_and_security_p"),
          i18n.t("legal.vendor_data_segregation_so_a_vendor"),
        ]; },
      },
      {
        heading: "3. Payments",
        get body() { return [
          i18n.t("legal.washpros_uses_stripe_pci_dss_level"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_breach_response"); },
        get body() { return [
          i18n.t("legal.if_washpros_becomes_aware_of_a"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_admin_controls"); },
        get body() { return [
          i18n.t("legal.washpros_operates_an_internal_admin_comman"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s6_reporting_a_vulnerability"); },
        body: [
          "If you believe you have found a security vulnerability in any WashPros surface, please report it to legal@washpros.io with the subject “Security Report — [short description]”. Do not exploit the vulnerability beyond what is necessary to demonstrate it, do not access data that does not belong to you, and do not publicly disclose the vulnerability until WashPros has had a reasonable opportunity to remediate.",
        ],
      },
    ],
    related: [REL.privacy, REL.retention, REL.cookie, REL.comms, REL.ai, REL.terms],
  },

  // ─────────────────────────────────────────────────────────────
  "cookie-policy": {
    id: "cookie-policy",
    get title() { return i18n.t("legal.cookie_policy"); },
    get badge() { return i18n.t("legal.web_tracking"); },
    badgeIcon: "Cookie",
    get summary() { return i18n.t("legal.how_and_why_washpros_io_uses"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_what_are_cookies"); },
        get body() { return [
          i18n.t("legal.cookies_are_small_text_files_placed"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_how_we_categorize_cookies"); },
        get body() { return [
          i18n.t("legal.washpros_uses_cookies_in_four_categories"),
        ]; },
        get items() { return [
          i18n.t("legal.essential_required_for_the_site_to"),
          i18n.t("legal.preferences_remember_non_essential_choices"),
          i18n.t("legal.analytics_measure_how_visitors_find_and"),
          i18n.t("legal.marketing_measure_the_performance_of_washp"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_third_party_services"); },
        get body() { return [
          i18n.t("legal.where_washpros_uses_a_third_party"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_your_choices"); },
        get body() { return [
          i18n.t("legal.you_can_clear_cookies_and_local"),
          i18n.t("legal.disabling_essential_cookies_will_prevent_y"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_do_not_track"); },
        get body() { return [
          i18n.t("legal.washpros_does_not_currently_respond_to"),
        ]; },
      },
    ],
    related: [REL.privacy, REL.comms, REL.retention, REL.dataSec, REL.terms],
  },

  // ─────────────────────────────────────────────────────────────
  "data-retention": {
    id: "data-retention",
    get title() { return i18n.t("legal.data_retention_policy"); },
    get badge() { return i18n.t("legal.data_lifecycle"); },
    badgeIcon: "Archive",
    get summary() { return i18n.t("legal.how_long_washpros_keeps_the_data"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_why_we_set_retention_limits"); },
        get body() { return [
          i18n.t("legal.storing_data_longer_than_necessary_creates"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_retention_by_data_type"); },
        get items() { return [
          i18n.t("legal.customer_account_profile_name_email_phone"),
          i18n.t("legal.booking_records_price_vendor_service_addre"),
          i18n.t("legal.pre_service_and_post_service_vehicle"),
          i18n.t("legal.in_app_chat_logs_between_customers"),
          i18n.t("legal.ai_phone_sky_call_transcripts_and"),
          i18n.t("legal.vendor_application_files_driver_license_co"),
          i18n.t("legal.outreach_leads_and_email_marketing_events"),
          i18n.t("legal.payment_metadata_last_4_of_card"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_legal_hold"); },
        get body() { return [
          i18n.t("legal.if_washpros_receives_a_valid_legal"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_how_to_request_deletion"); },
        body: [
          "Customers may request account deletion through the WashPros app or by emailing legal@washpros.io from the address on file. We will honor the request subject to the legal-hold and recordkeeping exceptions above. See also the Privacy Policy for additional rights under Florida and U.S. law.",
        ],
      },
    ],
    related: [REL.privacy, REL.dataSec, REL.comms, REL.cookie, REL.ai, REL.terms],
  },

  // ─────────────────────────────────────────────────────────────
  "reviews-policy": {
    id: "reviews-policy",
    get title() { return i18n.t("legal.reviews_policy"); },
    get badge() { return i18n.t("legal.marketplace_integrity"); },
    badgeIcon: "Star",
    get summary() { return i18n.t("legal.how_washpros_keeps_customer_reviews_honest"); },
    sections: [
      {
        heading: "1. The Rule",
        get body() { return [
          i18n.t("legal.washpros_reviews_are_a_signal_customers"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_prohibited_practices"); },
        get items() { return [
          i18n.t("legal.posting_a_review_for_a_booking"),
          i18n.t("legal.soliciting_reviews_in_exchange_for_cash"),
          i18n.t("legal.coordinating_a_group_of_reviewers_employee"),
          i18n.t("legal.retaliating_against_a_customer_who_left"),
          i18n.t("legal.posting_reviews_that_contain_illegal_conte"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_vendor_responses"); },
        get body() { return [
          i18n.t("legal.vendors_are_allowed_and_encouraged_to"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_washpros_role"); },
        get body() { return [
          i18n.t("legal.washpros_may_remove_a_review_that"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_report_a_review"); },
        body: [
          "If you believe a review on the WashPros Platform violates this policy, email legal@washpros.io with the booking ID (if any), the vendor name, and a description of the issue. Reports may be made by customers, by vendors, or by members of the public.",
        ],
      },
    ],
    related: [REL.acceptable, REL.influencer, REL.vendor, REL.terms, REL.privacy],
  },

  // ─────────────────────────────────────────────────────────────
  "influencer-disclosure": {
    id: "influencer-disclosure",
    get title() { return i18n.t("legal.influencer_affiliate_disclosure_policy"); },
    get badge() { return i18n.t("legal.marketing_transparency"); },
    badgeIcon: "Megaphone",
    get summary() { return i18n.t("legal.how_washpros_expects_influencers_affiliate"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_why_disclosure_matters"); },
        get body() { return [
          i18n.t("legal.customers_deserve_to_know_when_content"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_who_this_applies_to"); },
        get items() { return [
          i18n.t("legal.influencers_content_creators_and_podcast_h"),
          i18n.t("legal.affiliate_or_referral_partners_who_earn"),
          i18n.t("legal.washpros_employees_contractors_vendors_and"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_required_disclosure"); },
        get body() { return [
          i18n.t("legal.every_public_facing_piece_of_content"),
        ]; },
        get items() { return [
          i18n.t("legal.paid_partnership_with_washpros"),
          i18n.t("legal.ad_washpros_at_the_start_of"),
          i18n.t("legal.verbal_disclosure_within_the_first_30"),
          i18n.t("legal.inline_referral_link_next_to_any"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_honesty_requirement"); },
        get body() { return [
          i18n.t("legal.influencer_and_affiliate_content_must_refl"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_consequences_of_violation"); },
        get body() { return [
          i18n.t("legal.washpros_may_end_an_affiliate_or"),
        ]; },
      },
    ],
    related: [REL.reviews, REL.acceptable, REL.trademark, REL.terms, REL.privacy],
  },

  // ─────────────────────────────────────────────────────────────
  "trademark-policy": {
    id: "trademark-policy",
    get title() { return i18n.t("legal.trademark_policy"); },
    get badge() { return i18n.t("legal.brand_protection"); },
    badgeIcon: "Stamp",
    get summary() { return i18n.t("legal.how_washpros_licenses_polices_and_protects"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_washpros_marks"); },
        get items() { return [
          i18n.t("legal.washpros_registered_trademark_word_mark"),
          i18n.t("legal.washpros_double_droplet_icon_registered_tr"),
          i18n.t("legal.the_car_wash_concierge_company_trademark"),
          i18n.t("legal.wax_shine_headquarters_trademark_of_washpr"),
          i18n.t("legal.washpros_fleet_trademark_of_washpros_globa"),
          i18n.t("legal.washpros_is_a_registered_fictitious_name"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_use_of_washpros_marks"); },
        get body() { return [
          i18n.t("legal.the_washpros_marks_are_owned_exclusively"),
        ]; },
        get items() { return [
          i18n.t("legal.on_your_own_business_cards_vehicle"),
          i18n.t("legal.in_a_domain_name_sub_domain"),
          i18n.t("legal.in_paid_advertising_search_engine_keyword"),
          i18n.t("legal.in_merchandise_apparel_decals_drinkware_et"),
          i18n.t("legal.in_a_way_that_suggests_sponsorship"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_approved_vendor_affiliate_use"); },
        get body() { return [
          i18n.t("legal.approved_washpros_vendors_and_affiliates_m"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_fair_use_press"); },
        get body() { return [
          i18n.t("legal.journalists_analysts_podcasters_and_academ"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_reporting_infringement"); },
        body: [
          "If you see what appears to be unauthorized use of a WashPros mark — for example, a cloned brand, a fake mobile-wash van using our droplet icon, or a deceptive app store listing — please email legal@washpros.io with the URL, location, screenshots, and any context you can safely share. Copyright-specific notifications follow the separate Copyright Notification Policy.",
        ],
      },
      {
        get heading() { return i18n.t("legal.s6_requesting_permission"); },
        body: [
          "To request a written license to use any WashPros mark, contact legal@washpros.io. Include the proposed use, the audience, the duration, the geography, and any draft creative for review.",
        ],
      },
    ],
    related: [REL.copyright, REL.acceptable, REL.influencer, REL.terms, REL.privacy],
  },

  // ─────────────────────────────────────────────────────────────
  "subscription-terms": {
    id: "subscription-terms",
    get title() { return i18n.t("legal.subscription_terms"); },
    get badge() { return i18n.t("legal.recurring_services"); },
    badgeIcon: "Repeat",
    get summary() { return i18n.t("legal.terms_that_apply_to_washpros_vip"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_plans_covered"); },
        get body() { return [
          i18n.t("legal.this_policy_applies_to"),
        ]; },
        get items() { return [
          i18n.t("legal.washpros_vip_customer_membership_and_simil"),
          i18n.t("legal.vendor_recurring_subscriptions_including_a"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_billing_cycle_renewal"); },
        get body() { return [
          i18n.t("legal.subscriptions_renew_automatically_at_the_e"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_cancellation"); },
        body: [
          "You may cancel your subscription at any time from your account settings or by emailing hello@washpros.io. Cancellation is effective at the end of the current paid period — meaning you keep the benefits you have already paid for, and you are not charged for the next period. WashPros does not pro-rate refunds for cancellation within a paid period unless required by law or expressly stated in the plan’s terms.",
        ],
      },
      {
        get heading() { return i18n.t("legal.s4_downgrades_upgrades"); },
        get body() { return [
          i18n.t("legal.you_may_upgrade_between_washpros_subscript"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_failed_payments"); },
        get body() { return [
          i18n.t("legal.if_a_subscription_renewal_fails_card"),
        ]; },
      },
      {
        heading: "6. Refunds",
        get body() { return [
          i18n.t("legal.washpros_does_not_issue_refunds_for"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s7_plan_changes_by_washpros"); },
        get body() { return [
          i18n.t("legal.washpros_may_change_the_benefits_price"),
        ]; },
      },
    ],
    related: [REL.refund, REL.comms, REL.acceptable, REL.terms, REL.privacy],
  },

  // ─────────────────────────────────────────────────────────────
  "vendor-payout-policy": {
    id: "vendor-payout-policy",
    get title() { return i18n.t("legal.vendor_payout_policy"); },
    get badge() { return i18n.t("legal.payments_to_vendors"); },
    badgeIcon: "Banknote",
    get summary() { return i18n.t("legal.how_and_when_washpros_pays_approved"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_commission_vendor_take"); },
        get body() { return [
          i18n.t("legal.each_washpros_service_category_has_a"),
          i18n.t("legal.the_active_commission_percentage_for_your"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_payout_cadence"); },
        get body() { return [
          i18n.t("legal.payouts_are_issued_on_a_weekly"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_holds_reserves"); },
        get body() { return [
          i18n.t("legal.washpros_may_temporarily_hold_all_or"),
        ]; },
        get items() { return [
          i18n.t("legal.an_open_damage_claim_dispute_or"),
          i18n.t("legal.onboarding_documentation_driver_license_co"),
          i18n.t("legal.suspicious_activity_sudden_volume_spikes_m"),
          i18n.t("legal.an_applicable_tax_authority_has_issued"),
        ]; },
        get bodyAfter() { return [
          i18n.t("legal.holds_are_time_limited_and_are"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_chargebacks"); },
        get body() { return [
          i18n.t("legal.if_a_customer_reverses_a_charge"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_tax_forms"); },
        get body() { return [
          i18n.t("legal.washpros_issues_u_s_tax_forms"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s6_disputed_bookings_refunds"); },
        get body() { return [
          i18n.t("legal.when_washpros_refunds_a_customer_in"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s7_disputed_payouts"); },
        body: [
          "If you believe a payout calculation is wrong, email hello@washpros.io within thirty (30) days of the payout date with the booking IDs in question. WashPros will reconcile in good faith and correct any verified error.",
        ],
      },
    ],
    related: [REL.vendor, REL.refund, REL.damage, REL.acceptable, REL.terms, REL.privacy],
  },

  // ─────────────────────────────────────────────────────────────
  "ai-disclosure": {
    id: "ai-disclosure",
    get title() { return i18n.t("legal.ai_disclosure"); },
    get badge() { return i18n.t("legal.automation_transparency"); },
    badgeIcon: "Bot",
    get summary() { return i18n.t("legal.where_washpros_uses_artificial_intelligenc"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_where_ai_is_used"); },
        get items() { return [
          i18n.t("legal.phone_receptionist_sky_answers_calls_to"),
          i18n.t("legal.support_chatbot_embedded_in_washpros_io"),
          i18n.t("legal.dispatch_and_surge_logic_uses_algorithmic"),
          i18n.t("legal.marketing_asset_rendering_the_washpros_mar"),
          i18n.t("legal.internal_admin_tools_health_probes_content"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_what_this_means_for_you"); },
        get body() { return [
          i18n.t("legal.when_you_interact_with_the_washpros"),
          i18n.t("legal.calls_and_chats_may_be_transcribed"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_escalating_to_a_human"); },
        get body() { return [
          i18n.t("legal.you_can_ask_for_a_human"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_limits_of_ai"); },
        get body() { return [
          i18n.t("legal.washpros_ai_is_designed_to_help"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_consent_recording"); },
        body: [
          "By calling +1 (912) 662-8377 or using the WashPros chat surfaces, you consent to the transcription described in this disclosure and in the Privacy Policy. If you do not consent, please email hello@washpros.io instead.",
        ],
      },
    ],
    related: [REL.comms, REL.privacy, REL.retention, REL.acceptable, REL.terms],
  },

  // ─────────────────────────────────────────────────────────────
  "photo-media-policy": {
    id: "photo-media-policy",
    get title() { return i18n.t("legal.photo_media_policy"); },
    get badge() { return i18n.t("legal.visual_evidence_marketing"); },
    badgeIcon: "Camera",
    get summary() { return i18n.t("legal.how_washpros_and_approved_vendors_capture"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_operational_photos"); },
        get body() { return [
          i18n.t("legal.approved_washpros_vendors_are_required_to"),
        ]; },
      },
      {
        heading: "2. Storage",
        get body() { return [
          i18n.t("legal.operational_photos_are_stored_on_washpros"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_dispute_evidence"); },
        get body() { return [
          i18n.t("legal.when_a_damage_or_service_quality"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_marketing_permission"); },
        get body() { return [
          i18n.t("legal.washpros_does_not_publish_a_customer"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_vendor_captured_media_off_platform"); },
        get body() { return [
          i18n.t("legal.vendors_may_take_their_own_photos"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s6_children_bystanders"); },
        get body() { return [
          i18n.t("legal.photos_and_videos_must_not_identify"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s7_removal_requests"); },
        body: [
          "If a marketing image that depicts your vehicle, property, or likeness appears on a WashPros surface and you would like it removed, email hello@washpros.io with a description and a link. WashPros honors reasonable removal requests within ten (10) business days, subject to legitimate evidentiary or legal-hold needs.",
        ],
      },
    ],
    related: [REL.damage, REL.vendor, REL.privacy, REL.retention, REL.copyright, REL.terms],
  },

  // ─────────────────────────────────────────────────────────────
  "property-damage-policy": {
    id: "property-damage-policy",
    get title() { return i18n.t("legal.property_damage_policy"); },
    get badge() { return i18n.t("legal.claims_resolution"); },
    badgeIcon: "AlertTriangle",
    get summary() { return i18n.t("legal.what_to_do_if_something_goes"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_pre_service_condition"); },
        get body() { return [
          i18n.t("legal.every_washpros_booking_begins_with_a"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_reporting_a_claim"); },
        body: [
          "If you believe your property was damaged during a WashPros booking, please report the incident through the WashPros app or by emailing hello@washpros.io within seventy-two (72) hours of the booking. Earlier reporting is better and produces better outcomes. Include:",
        ],
        get items() { return [
          i18n.t("legal.the_booking_id_visible_in_your"),
          i18n.t("legal.a_description_of_the_damage_and"),
          i18n.t("legal.clear_photographs_of_the_damaged_area"),
          i18n.t("legal.any_post_service_photos_already_in"),
          i18n.t("legal.if_applicable_a_repair_estimate_from"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_investigation"); },
        get body() { return [
          i18n.t("legal.washpros_reviews_the_booking_record_timest"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_responsibility"); },
        get body() { return [
          i18n.t("legal.washpros_vendors_operate_as_independent_bu"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_customer_cooperation"); },
        get body() { return [
          i18n.t("legal.customers_must_give_washpros_and_the"),
        ]; },
      },
      {
        heading: "6. Timing",
        get body() { return [
          i18n.t("legal.washpros_acknowledges_every_property_damag"),
        ]; },
      },
      {
        heading: "7. Disputes",
        get body() { return [
          i18n.t("legal.if_a_customer_disagrees_with_the"),
        ]; },
      },
    ],
    related: [REL.refund, REL.vendor, REL.photo, REL.acceptable, REL.terms, REL.privacy],
  },

  // ─────────────────────────────────────────────────────────────
  "dmca-agent": {
    id: "dmca-agent",
    get title() { return i18n.t("legal.dmca_designated_agent"); },
    get badge() { return i18n.t("legal.designated_agent"); },
    badgeIcon: "Gavel",
    get summary() { return i18n.t("legal.designated_agent_for_receipt_of_notificati"); },
    sections: [
      {
        get heading() { return i18n.t("legal.s1_designated_agent_contact"); },
        get body() { return [
          i18n.t("legal.washpros_has_designated_the_following_agen"),
        ]; },
        get items() { return [
          i18n.t("legal.designated_agent_washpros_legal_attn_dmca"),
          i18n.t("legal.service_provider_washpros_global_services"),
          i18n.t("legal.mailing_address_6800_bird_rd_suite"),
          "Email: legal@washpros.io",
          i18n.t("legal.subject_line_dmca_notification_your_name"),
          i18n.t("legal.dmca_registration_number_dmca_1072995"),
          i18n.t("legal.directory_u_s_copyright_office_dmca"),
          i18n.t("legal.status_submitted_for_inclusion"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s2_how_to_submit_a_notification"); },
        get body() { return [
          i18n.t("legal.washpros_procedure_for_submitting_a_notifi"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s3_counter_notifications"); },
        get body() { return [
          i18n.t("legal.if_your_content_was_removed_in"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s4_federal_registration"); },
        get body() { return [
          i18n.t("legal.washpros_has_submitted_its_designated_agen"),
        ]; },
      },
      {
        get heading() { return i18n.t("legal.s5_anti_abuse"); },
        get body() { return [
          i18n.t("legal.submitting_a_dmca_notification_with_knowle"),
        ]; },
      },
    ],
    related: [REL.copyright, REL.trademark, REL.acceptable, REL.terms, REL.privacy],
  },
};

// Sorted list for the /legal hub grid.
export const POLICY_ORDER = [
  "acceptable-use",
  "vendor-marketplace-standards",
  "booking-refund-dispute-policy",
  "communications-policy",
  "data-security",
  "cookie-policy",
  "data-retention",
  "reviews-policy",
  "influencer-disclosure",
  "trademark-policy",
  "subscription-terms",
  "vendor-payout-policy",
  "ai-disclosure",
  "photo-media-policy",
  "property-damage-policy",
  "dmca-agent",
];
